/**
 * /oauth/callback — Google / Apple / GitHub から戻る画面（2026-09-27 creator/dev-oauth-ui）。
 *
 *   /oauth/callback?s=<画面の state>#code=…&provider=…   成功（コードは fragment = 配信サーバー・CDN のログに残らない）
 *   /oauth/callback?s=<画面の state>#error=…&provider=…  失敗（email_exists 等・サーバーの resolve.ts の DevOAuthError）
 *
 * 1. URL を読んだらすぐ消す（reset-password / verify-email と同じ = AccountRecovery.tsx の useFragmentToken。画面共有・履歴に残さない）
 * 2. s を sessionStorage の控え（devOAuth.ts の PendingOAuth）と比べる。合わなければ交換しない（このタブで始めた往復ではない）
 * 3. POST /v1/developers/oauth/exchange { code, verifier } → 始めた目的（intent）で分ける:
 *      login  → ログインして returnTo へ（takeover: true なら先に「前の手段を無効にした」を見せる）
 *      link   → /account へ（「追加しました」）
 *      reauth → reauthToken をメモリに置いて returnTo へ（呼んだ画面が続きを実行する・components/Reauth.tsx）
 *    失敗 → login は /login に案内を出す（email_exists・verify_email_first 等）、link は /account、reauth は returnTo に失敗を渡す
 *
 * 招待コード（2026-09-29 creator/launch-money・inviteCode.ts）: 登録の画面で入れたコードは控え（PendingOAuth.inviteCode）で持ってきて、
 *   login の exchange に付ける。応答の invite が ignored（既存のアカウントへのログインだった）なら「残高の画面から入れられます」を見せてから進む。
 *   コードのせいで入れなかった（invalid_invite_code・invite_code_used・invite_limit_reached）ときは /signup へ戻して文言を出す
 *   （アカウントは作られていない = サーバーが TX ごとロールバック）。
 *
 * App.tsx はこのパスだけ、ログインの有無に関係なくこの画面を出す（ログインした瞬間に別の Routes に作り直されて、
 * 途中の状態（takeover の案内）が消えないように）。
 */
import { startTransition, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, getToken, type Developer } from '../api';
import { afterLoginPath } from '../authForms';
import { LanguageSwitch, Logo, Spinner } from '../components/ui';
import { PRODUCT_NAME } from '../productName';
import { CALLBACK_PATH, normalizeCallbackError, parseCallback, takePending, type CallbackError, type DevOAuthProvider, type PendingOAuth } from '../devOAuth';
import { pendingStorage, setResumedFailure, setResumedReauth } from '../devOAuthClient';
import { useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { deviceStorage, recordLogin } from '../lastLogin';
import { markAdVisit } from '../adVisit';
import { exchangeBody, inviteNotAppliedReason, inviteOutcome, isInviteError, type InviteError, type InviteSignupNotice } from '../inviteCode';
import { INVITE_CODE_PATH, InviteNotAppliedNotice } from '../components/InviteNotAppliedNotice';
import { useSession } from '../session';

/** /login に渡す案内（LoginPage が文言を出す） */
export interface OAuthLoginNotice {
  oauthError: CallbackError;
  provider: DevOAuthProvider;
  from?: string;
}
/** /account に渡す案内（pages/AccountLoginMethods.tsx が文言を出す） */
export type OAuthAccountNotice = { kind: 'linked'; provider: DevOAuthProvider } | { kind: 'error'; code: CallbackError; provider: DevOAuthProvider };

/**
 * 読んだ結果をモジュールに持つ。WHY: StrictMode（開発）は state の初期化と effect を 2 回走らせる。控えは一回限り（読んだら消す）
 * で、コードも一回限りなので、2 回目で「控えが無い」「コードが使用済み」にならないよう、1 回目の結果を使い回す
 */
let parsed: { key: string; params: ReturnType<typeof parseCallback>; pending: PendingOAuth | null } | null = null;
const started = new Set<string>();

function readOnce() {
  const key = window.location.search + window.location.hash;
  if (!parsed || (key && parsed.key !== key)) {
    const params = parseCallback(window.location.hash, window.location.search);
    parsed = { key, params, pending: takePending(pendingStorage(), params.state) };
  }
  return parsed;
}

/** takeover = 前の手段を無効にした案内・inviteIgnored = 招待コードは使われなかった（既存のアカウント）の案内。両方のこともある */
type View =
  | { kind: 'working' }
  | { kind: 'notice'; to: string; takeover: boolean; inviteIgnored: boolean }
  // 招待なしの本体の新規登録で、付けたコードが使えなかったがアカウントはできた（2026-10-29 core/open-signup-build・components/InviteNotAppliedNotice.tsx）
  | { kind: 'notApplied'; to: string; reason: InviteError | 'unknown'; fromAd: boolean }
  | { kind: 'invalid' };

export function OAuthCallbackPage() {
  const { t } = useT();
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [{ params, pending }] = useState(readOnce);
  const [view, setView] = useState<View>({ kind: 'working' });

  useEffect(() => {
    // 1. URL から code / error / s を消す（読んだ値は上で持っている）
    if (window.location.hash || window.location.search) window.history.replaceState(window.history.state, '', CALLBACK_PATH);

    const onceKey = params.code ?? params.error ?? params.state ?? '';
    if (started.has(onceKey)) return;
    started.add(onceKey);

    // 2. このタブで始めた往復か（控えが無い = 期限切れ・別のタブ・直接開いた）
    if (!pending || (params.provider && params.provider !== pending.provider)) {
      setView({ kind: 'invalid' });
      return;
    }
    const provider = pending.provider;
    const returnTo = afterLoginPath(pending.returnTo);
    const fail = (raw: string | null | undefined) => {
      // 招待コードで断られた = 登録の画面へ戻してコードを直してもらう（inviteCode.ts の InviteSignupNotice の WHY）
      // 広告から来た登録（控えの fromAd・2026-10-18）なら印を渡す = 登録の画面が「枠が埋まった」の文を選べる（inviteCode.ts の inviteErrorKey）
      if (pending.intent === 'login' && isInviteError(raw)) {
        const state: InviteSignupNotice = { inviteError: raw, ...(pending.fromAd === true ? { fromAd: true } : {}) };
        navigate('/signup', { replace: true, state });
        return;
      }
      const code = normalizeCallbackError(raw);
      if (pending.intent === 'login') {
        const state: OAuthLoginNotice = { oauthError: code, provider, from: returnTo };
        navigate('/login', { replace: true, state });
      } else if (pending.intent === 'link') {
        const state: { oauthNotice: OAuthAccountNotice } = { oauthNotice: { kind: 'error', code, provider } };
        navigate('/account', { replace: true, state });
      } else {
        setResumedFailure({ action: pending.action ?? '', code, provider });
        navigate(returnTo, { replace: true });
      }
    };
    if (params.error || !params.code) {
      fail(params.error);
      return;
    }

    // 招待コードは login の往復の控えにあるときだけ付く（inviteCode.ts の exchangeBody）
    const body = exchangeBody(pending, params.code);
    void (async () => {
      try {
        if (pending.intent === 'login') {
          const r = await api<{ token: string; developer: Developer; created: boolean; linked: boolean; takeover?: boolean; invite?: unknown; inviteNotApplied?: unknown }>('/v1/developers/oauth/exchange', {
            method: 'POST',
            body,
            auth: false
          });
          // 「最後に使ったログイン方法」をこの端末に覚える（lastLogin.ts・2026-09-28 creator/login-redesign）。
          // /login で「保存する」を外した端末では何も書かない（外した時点の印を lastLogin.ts が見る）。方法の名前だけでメールは持たない
          recordLogin(deviceStorage(), provider);
          // 広告から来た新規登録（控えの fromAd・2026-10-18 gtm/early-fixes）: この端末に印を残す = ホームの最初の頼み・つなぐ文の締めが
          //   「このゲームを公開して」になる（adVisit.ts の WHY）。既存のアカウントへのログイン（created: false）には付けない
          if (pending.fromAd === true && r.created) markAdVisit(deviceStorage(), Date.now());
          const inviteIgnored = inviteOutcome(r.invite).kind === 'ignored';
          // 招待なしの本体の新規登録で、コードが使えなかった（サーバーは新規登録の時だけ付ける）。この画面はログイン後も同じまま出る = 先に signIn してよい
          const notAppliedReason = r.created ? inviteNotAppliedReason(r) : null;
          if (notAppliedReason) {
            signIn(r.token, r.developer);
            setView({ kind: 'notApplied', to: '/', reason: notAppliedReason, fromAd: pending.fromAd === true });
            return;
          }
          if (r.takeover || inviteIgnored) {
            // 先にログインだけ済ませ、案内を読んでから進んでもらう（この画面は App.tsx でログイン後も同じまま出る）
            signIn(r.token, r.developer);
            setView({ kind: 'notice', to: returnTo, takeover: !!r.takeover, inviteIgnored });
            return;
          }
          // LoginPage と同じ: navigate と signIn を同じ transition に（履歴を二重にしない）
          startTransition(() => {
            navigate(r.created ? '/' : returnTo, { replace: true });
            signIn(r.token, r.developer);
          });
          return;
        }
        // link / reauth は開始したのと同じセッション（kds_）で換える。
        // WHY auth: false + 手で Authorization か: api() は 401 で「セッション切れ」としてログアウトさせる。ここの 401 は
        //   「コードが期限切れ・使用済み」でもあり、それでログアウトさせると元の画面（閉鎖・出金の途中）が消える
        const headers: Record<string, string> = {};
        const token = getToken();
        if (token) headers.authorization = `Bearer ${token}`;
        if (pending.intent === 'link') {
          await api('/v1/developers/oauth/exchange', { method: 'POST', body, auth: false, headers });
          const state: { oauthNotice: OAuthAccountNotice } = { oauthNotice: { kind: 'linked', provider } };
          navigate('/account', { replace: true, state });
          return;
        }
        const r = await api<{ reauthToken: string; expiresAt: string }>('/v1/developers/oauth/exchange', { method: 'POST', body, auth: false, headers });
        setResumedReauth({ action: pending.action ?? '', reauthToken: r.reauthToken, expiresAt: String(r.expiresAt), provider });
        navigate(returnTo, { replace: true });
      } catch (err) {
        fail((err as ApiError).code);
      }
    })();
  }, [navigate, params, pending, signIn]);

  return (
    <main className="auth auth-single">
      <section className="auth-panel">
        <div className="auth-form">
          <div className="auth-form-top">
            <div className="auth-mobile-brand">
              <Logo size={28} />
              <span>{PRODUCT_NAME}</span>
            </div>
            <LanguageSwitch className="auth-lang" />
          </div>
          {view.kind === 'working' && <Spinner label={t('oauth.callback.working')} />}
          {view.kind === 'invalid' && (
            <>
              <h2>{t('oauth.callback.invalidTitle')}</h2>
              <p className="alert alert-error" role="alert">
                {t('oauth.error.invalid_state')}
              </p>
              <p className="auth-switch">
                <Link to="/login">{t('recover.backToLogin')}</Link>
              </p>
            </>
          )}
          {view.kind === 'notApplied' && (
            <InviteNotAppliedNotice
              reason={view.reason}
              fromAd={view.fromAd}
              onContinue={() => navigate(view.to, { replace: true })}
              onBilling={() => navigate(INVITE_CODE_PATH, { replace: true })}
            />
          )}
          {view.kind === 'notice' && (
            <>
              {view.takeover ? (
                <>
                  <h2>{t('oauth.takeover.title')}</h2>
                  <p className="alert alert-warn" role="status">
                    {t('oauth.takeover.body')}
                  </p>
                  {/* 出金用のパスキーと出金の確認の保留の話 = 出金（payouts）を見せない間は出さない（2026-09-29 creator/launch-money） */}
                  {!isLaunchHidden('payouts') && <p className="muted small">{t('oauth.takeover.hold')}</p>}
                </>
              ) : (
                <h2>{t('invite.ignored.title')}</h2>
              )}
              {view.inviteIgnored && (
                <p className="alert alert-info" role="status">
                  {t('invite.ignored.body')}
                </p>
              )}
              <button type="button" className="btn btn-primary btn-block" onClick={() => navigate(view.to, { replace: true })}>
                {t('recover.continue')}
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
